import { useEffect, useRef, useState, type ReactNode } from "react";

import { ApiError, api } from "../api/client";
import type { Building, Unit } from "../api/types";
import { countyName } from "../lib/counties";
import { associationBasis, associationLabel, associationTone } from "../lib/association";
import {
  CASE_UNITS_LABEL,
  NO_CASE_REGISTER,
  caseCountValue,
  caseUnitsValue,
} from "../lib/caseUnits";
import { closedCaseBasis, closedCaseLabel } from "../lib/closedCases";
import { milestoneCopy } from "../lib/milestone";
import {
  hasSirsAssessment,
  sirsAssessmentBasis,
  sirsAssessmentLabel,
  sirsAssessmentWhen,
} from "../lib/sirsAssessment";
import { Link } from "../router";
import { useScoring } from "../hooks/useScoring";
import { BuildingScore, UnitScore } from "./ScoreBreakdown";

interface Props {
  building: Building;
  canSeeUnits: boolean;
  /** See BuildingsTable — whether there is a folio to print. */
  canSeeFolio: boolean;
  onClose: () => void;
}

export function BuildingDrawer({ building, canSeeUnits, canSeeFolio,
                                 onClose }: Props) {
  const [units, setUnits] = useState<Unit[] | null>(null);
  const rules = useScoring(units?.flatMap((unit) => unit.flags) ?? []);
  const [error, setError] = useState("");
  const closeRef = useRef<HTMLButtonElement>(null);
  const caseUnits = caseUnitsValue(building);
  const sirsWhen = sirsAssessmentWhen(building);
  const hasClosedCases = building.closed_cases !== null;
  const hasAssociation = Boolean(building.association);

  useEffect(() => {
    if (!canSeeUnits) return;
    const controller = new AbortController();
    setUnits(null);
    setError("");
    api
      .units({ parent_folio: building.parent_folio, limit: 500 },
             controller.signal)
      .then((response) => setUnits(response.rows))
      .catch((cause) => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setError(cause instanceof ApiError ? cause.message : "Couldn't load units.");
      });
    return () => controller.abort();
  }, [building.parent_folio, canSeeUnits]);

  // Escape closes, and focus moves into the drawer so a keyboard user isn't
  // left behind on the table underneath.
  useEffect(() => {
    closeRef.current?.focus();
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <button className="scrim" onClick={onClose} aria-label="Close detail" />
      <aside className="drawer" role="dialog" aria-modal="true"
             aria-label={`Detail for ${building.address ?? "a building"}`}>
        <button className="linkbtn" onClick={onClose} ref={closeRef}>
          ← Back to list
        </button>

        <div className="eyebrow" style={{ marginTop: 16 }}>
          {building.city || countyName(building.county_fips) || "Unknown jurisdiction"}
        </div>
        <h2>{building.address ?? "Address unknown"}</h2>
        <div className="sub">
          {canSeeFolio
            ? `Folio ${building.parent_folio}`
            : "Parcel folio on a paid plan"}
        </div>

        <dl className="kv">
          <Cell
            label="Distress score"
            value={<BuildingScore building={building} rules={rules} />}
          />
          {/* A share only where a live register reaches: see lib/caseUnits. */}
          <Cell
            label={CASE_UNITS_LABEL}
            value={caseUnits.words
              ? <span className="kv-note">{caseUnits.value}</span>
              : caseUnits.value}
          />
          <Cell
            label="Built"
            value={`${building.year_built ?? "—"} · ${building.age ? `${building.age} yrs` : "—"}`}
          />
          <Cell label="Floors" value={building.derived_floors?.toString() ?? "unknown"} />
          <Cell label="Unsafe structure"
                value={<CaseCount building={building}
                                  count={building.unsafe_structure_cases} />} />
          <Cell label="Expired permits"
                value={<CaseCount building={building}
                                  count={building.expired_permit_cases} />} />
          <Cell label="Violation liens"
                value={<CaseCount building={building}
                                  count={building.lien_cases} />} />
          <Cell label="Absentee owners" value={String(building.absentee_units)} />
          {/* The report behind the building's SIRS points, so they can be
              checked. Full width, and after an even count of cells so the
              grid keeps no hole. Absent where no report is joined, which is
              not "none was filed". */}
          {hasSirsAssessment(building) && (
            <Cell
              wide
              label="SIRS special assessment"
              value={
                <span title={sirsAssessmentBasis(building)}>
                  {sirsAssessmentLabel(building)}
                  <span className="kv-sub">
                    {sirsWhen ? `${sirsWhen} · ` : ""}as reported to DBPR
                  </span>
                </span>
              }
            />
          )}
          {/* Rendered only where the county register reaches this building.
              Everywhere else the field is null and the cell is absent — a
              "—" would read as a register that came back empty. This one and
              Association are the grid's two optional half cells: either one
              alone takes the full row, or it leaves a hole beside it (White
              Pelican, Lee, 29 Sep 2026). */}
          {hasClosedCases && (
            <Cell
              wide={!hasAssociation}
              label="Cases closed"
              value={
                <span title={closedCaseBasis(building.closed_cases)}>
                  {closedCaseLabel(building.closed_cases,
                                   building.median_days_to_close)}
                </span>
              }
            />
          )}
          {/* Only where the DBPR register reaches this building. Absent is
              not "no association" — it is a development the register's one
              address did not join, which is four buildings in five. */}
          {building.association && (
            <Cell
              wide={!hasClosedCases}
              label="Association"
              value={
                <span title={associationBasis(building.association)}>
                  {associationLabel(building.association)}
                </span>
              }
            />
          )}
        </dl>

        {/* An inactive corporation with nobody carrying its name on is worth
            a sentence rather than a tooltip — and the sentence says it is a
            filing status, because that is all the file says. */}
        {associationTone(building.association) !== "none" && (
          <div className="notice neutral">
            <div className="notice-head">
              <strong>Association inactive on Sunbiz</strong>
              <span className="badge b-grey">State filing</span>
            </div>
            <p className="notice-basis">{associationBasis(building.association)}</p>
          </div>
        )}

        {/* The basis line is not decoration. A county case record and our
            floor-count inference are different kinds of claim, and presenting
            them alike about a named association is the sharpest liability in
            this product — see Terms §8.3. */}
        <MilestoneNotice building={building} />
        <RecertHistory building={building} />

        <h3 className="eyebrow" style={{ margin: "24px 0 10px" }}>
          Units carrying a signal
        </h3>

        {!canSeeUnits ? (
          <p style={{ fontSize: 13, color: "var(--ink-2)" }}>
            Unit-level detail starts on the County plan.
          </p>
        ) : error ? (
          <div className="err">{error}</div>
        ) : !units ? (
          <p className="sub">Loading…</p>
        ) : units.length === 0 ? (
          <p className="sub">No scored units in this building.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Unit</th>
                <th>Owner</th>
                <th className="num">
                  <Link to="/methodology" className="th-link">Score</Link>
                </th>
              </tr>
            </thead>
            <tbody>
              {units.map((unit) => (
                <tr key={unit.folio}>
                  <td className="mono">{unit.unit || unit.site_addr}</td>
                  <td style={{ fontSize: "12.5px" }}>{unit.owner}</td>
                  <td className="num">
                    <UnitScore unit={unit} rules={rules} placement="up" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </aside>
    </>
  );
}

/** A case count, or "Not covered" where no register reaches — see
 *  lib/caseUnits. The words carry the sentence the units cell says. */
function CaseCount({ building, count }: { building: Building; count: number }) {
  const { value, words } = caseCountValue(building, count);
  return words
    ? <span className="kv-note" title={NO_CASE_REGISTER}>{value}</span>
    : <>{value}</>;
}

function Cell({ label, value, wide = false }: {
  label: string;
  value: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={wide ? "kv-wide" : undefined}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function MilestoneNotice({ building }: { building: Building }) {
  const copy = milestoneCopy(building);
  // Only a real determination that the statute does not reach this building is
  // silent. "Unknown" is neutral too and must still be said out loud — saying
  // nothing is what made it indistinguishable from "no obligation".
  if (building.milestone_status === "not_eligible"
      && building.milestone_source !== "county") {
    return null;
  }

  return (
    <div className={`notice ${copy.tone}`}>
      <div className="notice-head">
        <strong>{copy.headline}</strong>
        <span className={`badge ${copy.authoritative ? "b-red" : "b-grey"}`}>
          {copy.provenance}
        </span>
      </div>
      <p className="notice-basis">{copy.basis}</p>
      {!copy.authoritative && copy.tone !== "clear" && (
        <p className="notice-basis">
          Confirm with the building department for this jurisdiction before
          relying on it.
        </p>
      )}
    </div>
  );
}

function RecertHistory({ building }: { building: Building }) {
  if (building.milestone_source !== "county") return null;
  return (
    <p className="sub" style={{ marginTop: 8 }}>
      Recertification {building.recert_status}
      {building.recert_year ? ` · ${building.recert_year}` : ""}
      {building.recert_enforcement ? " · referred to enforcement" : ""}
    </p>
  );
}
